// -----------------------------------------------------------------------------
// src/pages/partnerships/PartnershipsPage.js – FULL REVISED FILE
// -----------------------------------------------------------------------------

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Paper,
  Drawer,
  Badge,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OfferIcon from "@mui/icons-material/LocalOffer";
import PayoutIcon from "@mui/icons-material/MonetizationOn";
import MenuIcon from "@mui/icons-material/Menu";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ShareIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import DeliverableIcon from "@mui/icons-material/AssignmentTurnedIn";

import { useMutation, useQuery, useQueryClient } from "react-query";
import client from "../../../../API";
import SendOfferDialog from "./smallercomps/sendoffer";
import PayoutDialog from "./smallercomps/payout";
import OfferDialog from "./smallercomps/offerdialog";
import PartnershipsDetailView from "./detailview";
import AlertDialog from "../../../../Components/AlertDialog";
import useAlertDialog from "../../../../Components/useAlertDialog";
import ChatScreen from "../../../../Components/ChatScreen";
import ParticipantsPanel from "../../../../Components/ChatComponents/chatextra";
import { globalStyles } from "../../../../Utils/Styles";

const PartnershipsPage = () => {
  const { partnershipId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const chatRef = useRef(null);

  /* ------------------------------------------------------------------------ */
  /* UI state                                                                 */
  /* ------------------------------------------------------------------------ */
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [sendOfferOpen, setSendOfferOpen] = useState(false);
  const [sendPayoutOpen, setSendPayoutOpen] = useState(false);
  const [viewOfferOpen, setViewOfferOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmails, setShareEmails] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [deliverableBadge, setDeliverableBadge] = useState(0);

  const handleToggleParticipants = () => setParticipantsOpen((p) => !p);

  /* ------------------------------------------------------------------------ */
  /* Alerts                                                                   */
  /* ------------------------------------------------------------------------ */
  const { dialogState, openDialog } = useAlertDialog();

  /* ------------------------------------------------------------------------ */
  /* Fetch partnership & creator data                                         */
  /* ------------------------------------------------------------------------ */
  const {
    data: partnershipFetch,
    isLoading: loading,
    error: partnershipError,
    refetch: partnershipRefetch,
  } = useQuery(
    ["partnerships", partnershipId],
    () => client.partnerships.fetchPartnership(partnershipId),
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (partnershipError) {
      console.error("Error fetching partnership details:", partnershipError);
      const msg =
        partnershipError.response?.data?.error || partnershipError.message;
      openDialog("Error", `Error while fetching partnership: ${msg}`);
    }
  }, [partnershipError]);

  /* ------------------------------------------------------------------------ */
  /* Derived data                                                             */
  /* ------------------------------------------------------------------------ */
  const partnershipData = partnershipFetch?.partnership ?? { shared_with: [] };
  const partnershipName = partnershipData?.name ?? "N/A";
  const creator = {
    name: partnershipData?.creator ?? "Unknown",
    pfphref: partnershipData?.pfphref || "/placeholder.png",
  };

  /* ------------------------------------------------------------------------ */
  /* Conversation bootstrap (create if missing)                               */
  /* ------------------------------------------------------------------------ */
  const conversationId = partnershipData?.conversation_id ?? null;

  const createConversation = useMutation(
    client.conversations.createPartnership,
    { onSuccess: () => partnershipRefetch() }
  );

  useEffect(() => {
    if (partnershipFetch && !conversationId) {
      createConversation.mutate({ partnership_id: partnershipId });
    }
  }, [partnershipFetch]);

  /* ------------------------------------------------------------------------ */
  /* Partnership status helpers                                               */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!loading && partnershipData?.status?.toLowerCase() === "pending") {
      setPendingDialogOpen(true);
    }
  }, [loading, partnershipData]);

  const updateStatusMutation = useMutation(
    (status) => client.partnerships.updateStatus(partnershipId, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["partnerships", partnershipId]);
        setConfirmDialogOpen(false);
      },
    }
  );

  /* ------------------------------------------------------------------------ */
  /* Share partnership (legacy flow)                                          */
  /* ------------------------------------------------------------------------ */
  const sharePartnershipMutation = useMutation(
    (sharedWith) =>
      client.partnerships.share(partnershipId, { shared_with: sharedWith }),
    {
      onSuccess: () => {
        setShareDialogOpen(false);
        setShareEmails("");
        partnershipRefetch();
      },
    }
  );

  const handleShareSubmit = () => {
    const emails = shareEmails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length) sharePartnershipMutation.mutate(emails);
  };

  const handleDeliverableStats = (deliverables) => {
    const count = deliverables.filter((d) => {
      const s = (d.deliverable?.status || "").toLowerCase();
      return s === "pending" || s === "accept" || s === "accepted";
    }).length;
    setDeliverableBadge(count);
  };

  /* ------------------------------------------------------------------------ */
  /* Early loading state                                                      */
  /* ------------------------------------------------------------------------ */
  if (loading) {
    return (
      <Typography
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Loading partnership details...
      </Typography>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */
  return (
    <Box
      sx={{
        ...globalStyles.heightMinusToolbar,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "clip",
      }}
    >
      <AlertDialog alertState={dialogState} />

      {/* ------------------------------------------------------------------- */}
      {/* Header                                                              */}
      {/* ------------------------------------------------------------------- */}
      <Paper
        elevation={4}
        square
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* BACK ARROW */}
        <IconButton
          onClick={() => navigate("/collabs")}
          sx={{ mr: 1, flexShrink: 0 }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* LEFT : avatar + boxed text */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          <Avatar src={creator.pfphref} alt={creator.name} />

          {/* BOXED-IN TITLE / DESCRIPTION */}
          <Box
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.default",
              maxWidth: { xs: "100%", sm: 640, md: 720 },
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              Partnership with @{creator.name}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mb: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {partnershipName}
            </Typography>

            {partnershipData.description && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {partnershipData.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* RIGHT : actions */}
        <Box sx={{ ml: "auto", flexShrink: 0 }}>
          {deliverableBadge > 0 && (
            <Tooltip title="View deliverables needing action" arrow>
              <IconButton
                onClick={() => chatRef.current?.scrollToFirstDeliverable()}
                size="large"
              >
                <Badge badgeContent={deliverableBadge} color="error">
                  <DeliverableIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {conversationId && (
            <IconButton onClick={handleToggleParticipants}>
              <PersonAddIcon />
            </IconButton>
          )}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Pop-up menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => setViewDetailsOpen(true)}>
            View Partnership Details
          </MenuItem>
          {partnershipData.status === "Active" && (
            <MenuItem onClick={() => setConfirmDialogOpen(true)}>
              Complete Partnership
            </MenuItem>
          )}
        </Menu>
      </Paper>

      {/* ------------------------------------------------------------------- */}
      {/* Chat                                                                */}
      {/* ------------------------------------------------------------------- */}
      <ChatScreen
        ref={chatRef}
        conversationId={conversationId}
        onDeliverableClick={(offer) => {
          setSelectedOffer(offer);
          setViewOfferOpen(true);
        }}
        onDeliverablesUpdate={handleDeliverableStats}
        sx={{ flex: 1, minHeight: 0 }}
      />

      {/* ------------------------------------------------------------------- */}
      {/* Participants drawer                                                 */}
      {/* ------------------------------------------------------------------- */}
      <Drawer anchor="right" open={participantsOpen} onClose={handleToggleParticipants}>
        <ParticipantsPanel
          conversationId={conversationId}
          onClose={handleToggleParticipants}
        />
      </Drawer>

      {/* ------------------------------------------------------------------- */}
      {/* Dialogs                                                             */}
      {/* ------------------------------------------------------------------- */}
      <SendOfferDialog
        open={sendOfferOpen}
        onClose={() => setSendOfferOpen(false)}
        partnershipId={partnershipId}
        creatorId={creator.name}
        conversationId={conversationId}
      />
      <PayoutDialog
        open={sendPayoutOpen}
        onClose={() => setSendPayoutOpen(false)}
        partnershipId={partnershipId}
        creatorId={creator.name}
        conversationId={conversationId}
      />
      {selectedOffer && (
        <OfferDialog
          open={viewOfferOpen}
          onClose={() => setViewOfferOpen(false)}
          offer={selectedOffer}
        />
      )}
      <PartnershipsDetailView
        open={viewDetailsOpen}
        onClose={() => setViewDetailsOpen(false)}
        partnershipId={partnershipId}
        partnershipData={partnershipData}
        creator={{ name: creator.name }}
      />

      {/* Share partnership */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
        <DialogTitle>Share Partnership</DialogTitle>
        <DialogContent>
          {partnershipData.shared_with?.length ? (
            <>
              <Typography variant="subtitle1">Already shared with:</Typography>
              <ul>
                {partnershipData.shared_with.map((email) => (
                  <li key={email}>{email}</li>
                ))}
              </ul>
            </>
          ) : (
            <Typography variant="subtitle1">
              Nobody has been shared this yet!
            </Typography>
          )}
          <DialogContentText sx={{ mt: 2 }}>
            Enter email addresses, separated by commas.
          </DialogContentText>
          <TextField
            fullWidth
            variant="standard"
            value={shareEmails}
            onChange={(e) => setShareEmails(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleShareSubmit}
            disabled={sharePartnershipMutation.isLoading}
          >
            {sharePartnershipMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Share"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm completion */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirm Completion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark this partnership as completed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => updateStatusMutation.mutate("Completed")}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pending status info */}
      <Dialog
        open={pendingDialogOpen}
        onClose={() => setPendingDialogOpen(false)}
      >
        <DialogTitle>Your Partnership is still pending</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This partnership is currently pending. Wait for your partner to
            accept the offer to begin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnershipsPage;
